import { mainApi } from '../../../api/mainApi';
import { middlewareApi } from '../../middleware/middlewareApi';

import {
  Catalog,
  FinancialInstitutions,
  PaymentMethodsStrings,
  TypeApprovalReasons,
} from '../interfaces/catalogs.interfaces';
import { Disability } from '../interfaces/disabilities.interfaces';
import { Status, SubjectsStatusResponse } from '../interfaces/subjectsStatus.interface';
import { AccessState } from '../../../types';
import { Matter } from '../interfaces/matter.interface';

export const catalogTags = middlewareApi.enhanceEndpoints({
  addTagTypes: ['Catalog', 'CatalogBanks'],
});
// Define a service using a base URL and expected endpoints
export const catalogApi = catalogTags.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * @GET get childrens from catalog by acronym
     * @Permission catalogs-obtener-catalog
     */
    getCatalogByAcronym: builder.query<Catalog[], { profileId?: number; acronym: string }>({
      queryFn: async ({ profileId, acronym }, { getState }) => {
        const {
          access: { currentProfile },
        } = getState() as { access: AccessState };
        const user_profile_id = currentProfile?.profileId;

        try {
          const { data } = await mainApi.get<Catalog>(`/catalogs/${acronym}/children`, {
            params: {
              user_profile_id: profileId || user_profile_id,
            },
          });

          return { data: Array.isArray(data.children) ? data.children : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Catalog', id } as const)),
              { type: 'Catalog', id: 'LIST' },
            ]
          : [{ type: 'Catalog', id: 'LIST' }],
    }),

    /**
     * @GET get catalog by acronym
     * @Permission catalogs-obtener-catalog
     */
    getActivityByAcronym: builder.query<Catalog, { acronym: string }>({
      queryFn: async ({ acronym }, { getState }) => {
        const {
          access: { currentProfile },
        } = getState() as { access: AccessState };
        const user_profile_id = currentProfile?.profileId;

        try {
          const { data } = await mainApi.get(`/catalogs/${acronym}/children`, {
            params: {
              user_profile_id: user_profile_id,
            },
          });

          return { data: data };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @GET identification types
     */
    getCatalogByAcronymChildren: builder.query<Catalog[], { profileId?: number; acronym: string }>({
      queryFn: async ({ profileId, acronym }, { getState }) => {
        const {
          access: { currentProfile },
        } = getState() as { access: AccessState };
        const user_profile_id = profileId || currentProfile?.profileId;
        try {
          const { data } = await mainApi.get<Catalog>(`/catalogs/${acronym}/children-keywork`, {
            params: {
              user_profile_id,
            },
          });

          return { data: data.children };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @GET catalogs-keyword
     */
    getCatalogByKeyword: builder.query<Catalog[], { profileId?: number; keyword: string }>({
      queryFn: async ({ profileId, keyword }, { getState }) => {
        const {
          access: { currentProfile },
        } = getState() as { access: AccessState };
        const user_profile_id = currentProfile?.profileId || profileId;
        try {
          const { data } = await mainApi.get<Catalog>(`/catalogs/${keyword}/children-keywork`, {
            params: {
              user_profile_id: user_profile_id,
            },
          });

          return { data: data.children };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @GET subject by id
     *
     *
     * */

    getSubjectById: builder.query<Matter, { profileId?: number; id: number | string }>({
      queryFn: async ({ profileId, id }, { getState }) => {
        const {
          access: { currentProfile },
        } = getState() as { access: AccessState };
        const user_profile_id = currentProfile?.profileId || profileId;
        try {
          const { data } = await mainApi.get<Matter>(`/subjects/${id}`, {
            params: {
              user_profile_id: user_profile_id,
            },
          });

          return { data: data };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @GET subjects status
     */
    getSubjectStatus: builder.query<Status[], { profileId: number; search: string }>({
      queryFn: async ({ profileId, search }) => {
        try {
          const { data } = await mainApi.get<SubjectsStatusResponse>(`/category-status`, {
            params: {
              user_profile_id: profileId,
              search,
            },
          });

          return { data: data.data[0].status };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @GET disability availables
     */
    getDisabilityAvailable: builder.query<Disability[], { profileId?: number }>({
      queryFn: async ({ profileId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };
          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.get<Disability>(`/type-disabilities`, {
            params: {
              user_profile_id,
              data: 'all',
              sort: 'id',
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @GET catalogs approval reasons
     */
    getApprovalReasons: builder.query<Catalog[], { profileId?: number; type: TypeApprovalReasons }>(
      {
        queryFn: async ({ profileId, type }, { getState }) => {
          try {
            const {
              access: { currentProfile },
            } = getState() as { access: AccessState };
            const user_profile_id = profileId || currentProfile?.profileId;

            const { data } = await mainApi.get<Catalog[]>(`/catalogs-reasons-approval`, {
              params: {
                user_profile_id,
                type_reason: type,
              },
            });

            return { data: Array.isArray(data) ? data : [] };
          } catch (error: any) {
            return { error };
          }
        },
      }
    ),

    /**
     * @GET FInancial Institutions
     * @Permission financial-institutions-obtener-catalog
     */
    getFinancialInstitutionsCatalog: builder.query<
      FinancialInstitutions[],
      { profileId: number; acronym?: string }
    >({
      queryFn: async ({ profileId, acronym = 'fnor' }) => {
        try {
          const { data } = await mainApi.get<FinancialInstitutions>(
            `/financial-institutions/${acronym}/children`,
            {
              params: {
                user_profile_id: profileId,
              },
            }
          );

          return { data: data.children };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'CatalogBanks', id } as const)),
              { type: 'CatalogBanks', id: 'LIST' },
            ]
          : [{ type: 'CatalogBanks', id: 'LIST' }],
    }),

    /**
     * @POST Upload file
     */
    sendFile: builder.mutation<any, { file: File; profileId: number; typeDocument: number }>({
      queryFn: async ({ file, profileId, typeDocument }) => {
        try {
          const instance = new FormData();
          instance.append('files[]', file);
          instance.append('type_document', typeDocument.toString());
          instance.append('user_profile_id', String(profileId));

          const { data } = await mainApi.post<any>(`/teacher-requisition/upload/files`, instance, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @POST Upload file
     */
    getListedPaymentMethods: builder.query<
      Catalog[],
      { profileId?: number; catalog_array: PaymentMethodsStrings[] }
    >({
      queryFn: async ({ profileId, catalog_array }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.post<Catalog[]>(`/catalogs/keyword-select`, {
            user_profile_id,
            catalog_array,
          });
          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) => [{ type: 'Catalog', id: 'LIST' }],
    }),

    //
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetCatalogByKeywordQuery,
  useGetCatalogByAcronymChildrenQuery,
  useGetCatalogByAcronymQuery,
  useGetSubjectStatusQuery,
  useSendFileMutation,
  useGetDisabilityAvailableQuery,
  useGetFinancialInstitutionsCatalogQuery,
  useGetActivityByAcronymQuery,
  useGetSubjectByIdQuery,
  useLazyGetCatalogByAcronymChildrenQuery,
  useGetListedPaymentMethodsQuery,
  useGetApprovalReasonsQuery,
} = catalogApi;
